import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PdfView } from '../components/PdfView'
import { FieldBox } from '../components/FieldBox'
import {
  ApiError,
  createEnvelope, envelopeToResend, sentAtOnce, uploadDocument,
  type AuthenticatedSession, type CreatedRecipient, type FieldKind, type NormRect,
  type RecipientRole, type RoutingRuleInput, type UploadResult,
} from '../api'
import { OrganizationPicker, SentBy } from '../components/SentBy'
import { SubscriptionPaywall } from '../components/SubscriptionPaywall'
import { ConfirmSheet } from '../components/ConfirmSheet'
import { KeepFor } from '../components/KeepFor'
import { keptFor } from '../keepFor'
import {
  DEFAULT_OPTIONS, defaultFieldLabel, fieldDisplayName, KINDS, kindLabel, kindNeedsOptions,
  newRect, signerFieldPrompt, specFor,
} from '../fields'
import { WorkflowBuilder } from '../components/WorkflowBuilder'
import {
  canControlShowWhen, dropDanglingShowWhen, followPageForRecipient, locateFieldKey,
  firstSigningParty, optionsRefused, showWhenOnNonSigners, showWhenUnanswerable, signingLinkCount, signsDocument, whatIsMissing,
  type OptionsFault, type ShowWhenDraft, type ShowWhenFault,
} from '../newEnvelopeDraft'
import {
  assignCandidates, detectCandidates, fillLinesOf, isCovered, snapToLines,
  type Candidate, type FillLine,
} from '../placement'
import { readDocumentText } from '../pdfText'
import {
  envelopeCandidates, isRoutingThreshold, resentRouting, routingBranch, routingHint, routingOrders, routingOutcome,
  sentRowOf, sentTurnWords, type SentRow, type SentRule,
} from '../newEnvelopeRouting'
import { bytes } from '../format'
import {
  applyWorkflowDraft,
  draftFromRecipients,
  draftWithRouting,
  MAX_GROUP_MEMBERS,
  MAX_PARTICIPANTS,
  type EnvelopeRecipient,
} from '../workflow/draft'
import { fitsSimpleSidebar, needsWorkflowStep } from '../workflow/needsWorkflow'
import { WORKFLOW_TONES, type WorkflowDraft } from '../workflow/types'

type Step = 'upload' | 'workflow' | 'place' | 'sent'

/**
 * How many PDFs one envelope may hold. The same number the server enforces;
 * saying it here is what lets the sender be told before they wait for an
 * upload that will be refused.
 */
const MAX_DOCUMENTS = 10

/** How far a blank line reaches for a dragged box, in screen pixels. */
const SNAP_PX = { x: 16, y: 12 }

/**
 * Whether a box can be taken away.
 *
 * Anything but a signature, always. A signature only when the party keeps one
 * somewhere — it is what their seal is anchored to — and only when nothing else
 * of theirs is on that document, because what they type there is stamped in and
 * then sealed, and a document they fill in but do not sign would carry their
 * answers outside any signature of theirs.
 */
function canRemove(fields: DraftField[], at: number): boolean {
  const f = fields[at]
  if (!f) return false
  if (f.kind !== 'signature') return true
  const signatures = fields.filter((x) => x.kind === 'signature').length
  const othersHere = fields.some((x, i) => i !== at && x.documentIndex === f.documentIndex)
  return signatures > 1 && !othersHere
}

/** What to call a document in a sentence: the title for the first, its own name after. */
function nameOfDocument(docs: LoadedDocument[], at: number, title: string): string {
  if (at === 0) return title.trim() || docs[0]?.name || 'the first document'
  return docs[at]?.name ?? `document ${at + 1}`
}

/** A file's name without its extension, which is what to call the document. */
function documentName(file: File): string {
  return file.name.replace(/\.pdf$/i, '')
}

/** The PDFs in a file list, in the order they were chosen. */
function pdfsIn(files: FileList | null): File[] {
  return [...(files ?? [])].filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name))
}

/**
 * How the signatures are sequenced.
 *
 * These are the two arrangements that actually come up: everyone can sign
 * whenever they like, or each person waits for the one before them. They map
 * onto routing order — all 1, or 1..n — so the sender never has to think about
 * routing numbers to express what they want.
 */
type Order = 'parallel' | 'sequential'

interface DraftField {
  /** Stable across reorders and deletions, unlike the array index. */
  key: string
  kind: FieldKind
  label: string
  options: string[]
  required: boolean
  /** Which of the envelope's documents this box is on, counting from zero. */
  documentIndex: number
  page: number
  rect: NormRect
  /** False until the sender drags the box or picks its page. Until then it follows navigation. */
  placed: boolean
  /** Hide this box until another box has a particular answer. */
  showWhen?: ShowWhenDraft
}

interface GroupMember {
  key: string
  name: string
  email: string
}

interface Draft {
  key: string
  name: string
  email: string
  role: RecipientRole
  fields: DraftField[]
  /** When true, name is the group label and members hold the people. */
  isGroup?: boolean
  members?: GroupMember[]
}

const ROLE_LABELS: Record<RecipientRole, string> = {
  signer: 'Signer',
  cc: 'Receives a copy',
  needs_to_view: 'Needs to view',
  witness: 'Witness',
}

/** Every signer and witness starts with the signature box they are required to have. */
function newMember(): GroupMember {
  return { key: nextKey(), name: '', email: '' }
}

function newGroupDraft(
  index: number, page = 1, role: RecipientRole = 'signer', documentIndex = 0,
): Draft {
  return {
    ...newDraft(index, page, role, documentIndex),
    isGroup: true,
    email: '',
    members: [newMember(), newMember()],
  }
}

function newDraft(index: number, page = 1, role: RecipientRole = 'signer', documentIndex = 0): Draft {
  return {
    key: nextKey(),
    name: '',
    email: '',
    role,
    fields: signsDocument(role) ? [{
      key: nextKey(),
      kind: 'signature',
      label: '',
      options: [],
      required: true,
      documentIndex,
      page,
      rect: newRect('signature', index),
      placed: false,
    }] : [],
  }
}

/** One of the envelope's documents as the sender has it open. */
interface LoadedDocument {
  upload: UploadResult
  /** What the sender calls it. The first one is the envelope's title. */
  name: string
  /** A local object URL while it is being placed, so the bytes are not refetched. */
  url: string
}

/**
 * Changes one recipient's role, keeping the boxes the sender has already
 * placed wherever they still mean something.
 *
 * Both signing roles use the same boxes, so moving between them must not throw
 * placement away — a sender who has positioned four fields and then realises
 * the person is a witness should not have to place them again. Moving to a role
 * that signs nothing keeps the boxes in the draft but stops sending them, so
 * changing your mind back restores the work rather than losing it silently.
 */
function setRecipientRole(
  rs: Draft[], ri: number, role: RecipientRole, page: number, documentIndex: number,
): Draft[] {
  return rs.map((r, i) => {
    if (i !== ri || role === r.role) return r
    // Coming back to a signing role with nothing kept: give them the signature
    // box every signing party is required to have.
    if (signsDocument(role) && r.fields.length === 0) {
      return { ...newDraft(ri, page, role, documentIndex), name: r.name, email: r.email }
    }
    return { ...r, role }
  })
}

/** One colour per recipient, from the one list of them (workflow/types.ts). */
const toneFor = (i: number) => WORKFLOW_TONES[i % WORKFLOW_TONES.length]

let seq = 0
const nextKey = () => `k${++seq}`

/** What reading one document's text found, by its document key. */
interface Reading {
  candidates: Candidate[]
  lines: FillLine[]
}

/**
 * The roster with one suggestion accepted, and where its box is in the list.
 *
 * A signature replaces the person's signature on that document rather than
 * adding a second: the seal is anchored to one per document, so a second
 * signature suggestion for somebody is a better place for the first.
 */
function withSuggestion(rs: Draft[], c: Candidate, ri: number): { rs: Draft[]; f: number } {
  const r = rs[ri]
  const box = { documentIndex: c.documentIndex, page: c.page, rect: c.rect, placed: true }
  const existing = c.kind === 'signature'
    ? r.fields.findIndex((f) => f.kind === 'signature' && f.documentIndex === c.documentIndex)
    : -1
  const fields = existing >= 0
    ? r.fields.map((f, j) => (j === existing ? { ...f, ...box } : f))
    : [...r.fields, {
        key: nextKey(),
        kind: c.kind,
        label: c.label || defaultFieldLabel(c.kind, r.fields),
        options: [],
        required: true,
        ...box,
      }]
  return {
    rs: rs.map((x, i) => (i === ri ? { ...x, fields } : x)),
    f: existing >= 0 ? existing : fields.length - 1,
  }
}

function participationCount(rs: Draft[]) {
  return rs.reduce((n, r) => (r.isGroup && r.members ? n + r.members.length : n + 1), 0)
}

function draftComplete(r: Draft) {
  if (r.isGroup && r.members) {
    return r.name.trim() !== '' && r.members.every((m) => m.name.trim() && m.email.trim())
  }
  return r.name.trim() !== '' && r.email.trim() !== ''
}

export function NewEnvelope({ session }: { session: AuthenticatedSession }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // ?from=<envelope id> opens this form with a document already sent filled
  // in. There is no draft state to put a copy into — StatusDraft has been in
  // the schema since the first migration and nothing writes it — so a
  // duplicate is this form, prefilled, and the sender presses send. That is
  // also the safer shape: a duplicate that sent itself would re-send to the
  // same people without anybody looking.
  const from = params.get('from') ?? ''
  const [step, setStep] = useState<Step>('upload')
  // Several documents in one envelope: a contract with its schedules is sent
  // as one thing and signed as one act. The first is the envelope's own, which
  // is what the certificate and every existing chain are about; the rest sit
  // beside it in reading order.
  const [docs, setDocs] = useState<LoadedDocument[]>([])
  const [docIndex, setDocIndex] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [billingBlock, setBillingBlock] = useState<'subscription_required' | 'plan_limit_exceeded' | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const moreInput = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState('')
  // Which organization the envelope belongs to. Almost nobody belongs to more
  // than one, so it is defaulted and only asked about when it is a real choice.
  const [orgID, setOrgID] = useState(session.memberships[0]?.organization_id ?? '')
  const [recipients, setRecipients] = useState<Draft[]>([newDraft(0)])
  // Which box is being edited: a recipient and one of their fields.
  const [sel, setSel] = useState({ r: 0, f: 0 })
  const [order, setOrder] = useState<Order>('sequential')
  // The period the sender picked, if they picked one. What is sent is that,
  // when the chosen organization may keep a document that long, and the
  // organization's default otherwise (keptFor), so moving to an organization
  // with a shorter ceiling cannot carry a period it would refuse.
  const [chosenYears, setChosenYears] = useState<number | null>(null)
  const maxRetentionDays = session.billing?.find((b) => b.organization_id === orgID)?.max_retention_days
  const retentionYears = keptFor(maxRetentionDays, chosenYears)

  // The routing box, declared before the effect below that Send again sets it from.
  const [routingEnabled, setRoutingEnabled] = useState(false)
  const [routingThreshold, setRoutingThreshold] = useState('50000')
  const [routingFieldIndex, setRoutingFieldIndex] = useState(1)
  /** Send again found a routing rule this page cannot set up again. */
  const [routingLost, setRoutingLost] = useState(false)

  // Filling the form from a document already sent.
  //
  // The roles, the routing order and every box come across; the people do too,
  // because "send the same thing to the same people again" is the common case
  // and correcting a name is easier than typing four. Nothing is sent until
  // the sender presses send, and the fields keep their exact rectangles —
  // they are fractions of the page, so they land where they landed before.
  useEffect(() => {
    if (!from) return
    let cancelled = false
    setBusy(true)
    envelopeToResend(from)
      .then((prior) => {
        if (cancelled) return
        setDocs(prior.documents)
        setDocIndex(0)
        setTitle(prior.title)
        // A signing group comes back as one group: its members, and its boxes,
        // which the server keeps on the first of them.
        const parties = prior.parties
        if (parties.length) {
          const idToKey = new Map<string, string>()
          const keyToId = new Map<string, string>()
          const drafts: Draft[] = parties.map((p) => ({
            key: nextKey(),
            name: p.name,
            email: p.email,
            role: p.role,
            ...(p.members
              ? { isGroup: true, members: p.members.map((m) => ({ key: nextKey(), name: m.name, email: m.email })) }
              : {}),
            fields: prior.fields
              .filter((f) => p.participation_ids.includes(f.participation_id))
              .map((f) => {
                const key = nextKey()
                idToKey.set(f.id, key)
                keyToId.set(key, f.id)
                return {
                  key,
                  kind: f.kind,
                  label: f.label ?? '',
                  options: f.options ?? [],
                  required: f.required,
                  documentIndex: f.document_index,
                  page: f.page,
                  rect: { x: f.norm_x, y: f.norm_y, w: f.norm_w, h: f.norm_h },
                  // Placed, because somebody put them there on the document this
                  // came from. Treating them as unplaced would invite the sender
                  // to move boxes that are already right.
                  placed: true,
                }
              }),
          }))
          setRecipients(drafts.map((r) => ({
            ...r,
            fields: r.fields.map((f) => {
              const orig = prior.fields.find((x) => idToKey.get(x.id) === f.key)
              const ctrlKey = orig?.show_when?.field_id
                ? idToKey.get(orig.show_when.field_id)
                : undefined
              if (!ctrlKey || !orig?.show_when) return f
              const op = orig.show_when.op === 'eq' ? 'eq' as const : 'checked' as const
              return { ...f, showWhen: { fieldKey: ctrlKey, op, value: orig.show_when.value } }
            }),
          })))
          setSel({ r: firstSigningParty(drafts.map((d) => d.role)), f: 0 })
          setOrder(sentAtOnce(parties) ? 'parallel' : 'sequential')
          const routing = resentRouting(
            parties.map((p, i) => ({ ...p, fields: drafts[i].fields.map((f) => ({ id: keyToId.get(f.key) ?? '', kind: f.kind })) })),
            prior.routingRules,
          )
          setRoutingEnabled(routing.enabled)
          if (routing.enabled) {
            setRoutingThreshold(routing.threshold)
            setRoutingFieldIndex(routing.fieldIndex)
          }
          setRoutingLost(!routing.enabled && routing.lost)
        }
        setStep('place')
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not open that document again.'))
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [from])
  // Whether docsdone emails the links or hands them back. It is not a
  // convenience: a link docsdone sent to an address is evidence about that
  // mailbox, and the certificate says so. One handed back to the sender is
  // evidence about nothing but the sender, and the certificate says that too.
  const [deliverLinks, setDeliverLinks] = useState(false)
  // Whether docsdone chases the signers on its own. On by default, because the
  // point is that the sender stops remembering to; only offered when docsdone
  // emails the links, since a link the sender handed on is one docsdone has
  // nothing to send again.
  const [remindersEnabled, setRemindersEnabled] = useState(true)
  /** Set when the sender designed routing on the workflow canvas. */
  const [usedWorkflow, setUsedWorkflow] = useState(false)
  const [workflowDraft, setWorkflowDraft] = useState<WorkflowDraft | null>(null)
  const [sent, setSent] = useState<SentEnvelope | null>(null)
  const [copied, setCopied] = useState('')
  // What each document asks to be filled in, read from its own text. Kept by
  // document key rather than position, so removing one document does not hand
  // its suggestions to the next.
  const [readings, setReadings] = useState<Record<string, Reading>>({})
  const requested = useRef(new Set<string>())
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  // A person picked by the sender for a suggestion, over the one guessed. Held
  // by the recipient's key, not their position: reordering or removing a
  // signer would otherwise hand the pick to whoever moved into that place.
  const [chosenOwner, setChosenOwner] = useState<Record<string, string>>({})
  const [checking, setChecking] = useState(false)
  const documentArea = useRef<HTMLDivElement>(null)
  // The selected box's row in the roster, and a count of "Show the box" taps.
  // A tap selects the box, and its row only grows its "Show only when"
  // controls on the next render, so the scroll waits for that render.
  const activeRow = useRef<HTMLDivElement>(null)
  const [revealed, setRevealed] = useState<{ n: number; selector: string }>({ n: 0, selector: '' })

  // "Show the box" sits in the Review card, below the roster, and on a phone
  // a screen or more below the document. It changed only what was above the
  // screen, so the tap looked like it did nothing, and on a wide screen the
  // row growing above pushed the notice down under the pointer (review 4,
  // web-3 and design-1). What comes into view is what the notice asks the
  // sender to change: "Show only when" for a rule, and the option itself for
  // an options notice, which sits above that block and was left off the top
  // of a phone's screen (review 5, web-5). An option is also focused, so it
  // can be typed over at once.
  useEffect(() => {
    if (revealed.n === 0) return
    const row = activeRow.current
    const target = row?.querySelector<HTMLElement>(revealed.selector) ?? row
    target?.scrollIntoView({ block: 'center' })
    if (target instanceof HTMLInputElement) target.focus({ preventScroll: true })
  }, [revealed])

  useEffect(() => {
    for (const d of docs) {
      const key = d.upload.document_key
      if (requested.current.has(key)) continue
      requested.current.add(key)
      readDocumentText(d.url)
        .then((pages) => setReadings((m) => ({
          ...m,
          [key]: {
            candidates: detectCandidates(pages, 0, { fieldsSupported: d.upload.supports_fields })
              .map((c) => ({ ...c, id: `${key}:${c.id}` })),
            lines: fillLinesOf(pages),
          },
        })))
        // Suggestions are an aid. A document whose text cannot be read is
        // placed by hand exactly as before, and saying so would be noise.
        .catch(() => {})
    }
  }, [docs])

  const doc = docs[docIndex]?.upload ?? null
  const docUrl = docs[docIndex]?.url ?? ''
  // The weakest document governs: a filled-in value has to be drawable on every
  // document that carries one, and the server refuses the send otherwise.
  const supportsFields = docs.length ? docs.every((d) => d.upload.supports_fields) : true

  function patchRecipient(index: number, change: Partial<Draft>) {
    setRecipients((rs) => rs.map((r, i) => (i === index ? { ...r, ...change } : r)))
  }

  function patchField(ri: number, fi: number, change: Partial<DraftField>) {
    setRecipients((rs) => rs.map((r, i) => (
      i === ri ? { ...r, fields: r.fields.map((f, j) => (j === fi ? { ...f, ...change } : f)) } : r
    )))
  }

  function selectField(r: number, f: number) {
    setSel({ r, f })
    setRecipients((rs) => followPageForRecipient(rs, r, page, docIndex))
  }

  /**
   * Select a box, turn the document to its page, and scroll the part of its
   * row that a notice asks about into view (revealSelector).
   */
  function showBox(r: number, f: number, look: Reveal = { part: 'show-when' }) {
    const field = recipients[r]?.fields[f]
    if (!field) return
    setSel({ r, f })
    setDocIndex(field.documentIndex)
    setPage(field.page || 1)
    setRevealed((x) => ({ n: x.n + 1, selector: revealSelector(look, field.options) }))
  }

  /** Move the page view. Unplaced boxes for the selected signer follow. */
  function goToPage(next: number) {
    setPage(next)
    setRecipients((rs) => followPageForRecipient(rs, sel.r, next, docIndex))
  }

  /**
   * Show another of the envelope's documents, from its first page.
   *
   * Unplaced boxes do NOT follow. Turning a page and changing document are
   * different gestures: a sender who uploads a contract, then adds a schedule,
   * has not said the signature belongs on the schedule — and a box that moved
   * there would leave the contract with nothing to sign without anybody
   * touching it.
   */
  function goToDocument(next: number) {
    if (next < 0 || next >= docs.length) return
    setDocIndex(next)
    setPage(1)
  }

  function addField(kind: FieldKind) {
    const at = recipients[sel.r].fields.length
    setRecipients((rs) => rs.map((r, i) => (
      i === sel.r
        ? {
            ...r,
            fields: [...r.fields, {
              key: nextKey(), kind,
              label: defaultFieldLabel(kind, r.fields),
              options: kindNeedsOptions(kind) ? [...DEFAULT_OPTIONS] : [],
              required: true, documentIndex: docIndex, page, rect: newRect(kind, at),
              placed: true,
            }],
          }
        : r
    )))
    setSel({ r: sel.r, f: at })
  }

  function removeField(ri: number, fi: number) {
    if (!canRemove(recipients[ri].fields, fi)) return
    setRecipients((rs) => dropDanglingShowWhen(rs.map((r, i) => (
      i === ri ? { ...r, fields: r.fields.filter((_, j) => j !== fi) } : r
    ))))
    const next = Math.max(0, sel.f >= fi ? sel.f - 1 : sel.f)
    if (sel.r === ri) selectField(ri, next)
  }

  function addRecipient() {
    const r = recipients.length
    setRecipients((rs) => [...rs, newDraft(r, page, 'signer', docIndex)])
    selectField(r, 0)
  }

  function removeRecipient(index: number) {
    if (recipients.length === 1) return
    setRecipients((rs) => dropDanglingShowWhen(rs.filter((_, i) => i !== index)))
    selectField(Math.max(0, index === 0 ? 0 : index - 1), 0)
  }

  function moveRecipient(index: number, delta: number) {
    const to = index + delta
    if (to < 0 || to >= recipients.length) return
    setRecipients((rs) => {
      const next = [...rs]
      ;[next[index], next[to]] = [next[to], next[index]]
      return next
    })
    selectField(to, 0)
  }

  /** The first documents of a new envelope. Several at once is the common case. */
  async function onFiles(files: File[]) {
    if (!files.length) return
    // Checked here as well as when adding one later, and for the same reason:
    // the server refuses more than this, and the wait before it says so is
    // longest on this path — every file is uploaded first.
    if (files.length > MAX_DOCUMENTS) {
      setError(`An envelope can hold at most ${MAX_DOCUMENTS} documents.`)
      return
    }
    setError('')
    setBusy(true)
    try {
      const loaded = await Promise.all(files.map(async (file) => ({
        upload: await uploadDocument(file),
        name: documentName(file),
        // Rendered locally rather than refetched: the bytes are already here.
        url: URL.createObjectURL(file),
      })))
      setDocs(loaded)
      setDocIndex(0)
      if (!title) setTitle(loaded[0].name)
      setPage(1)
      setRecipients([newDraft(0)])
      setSel({ r: 0, f: 0 })
      setStep('place')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.')
    } finally {
      setBusy(false)
    }
  }

  /** Another document, added while placing boxes. */
  async function addDocuments(files: File[]) {
    if (!files.length) return
    if (docs.length + files.length > MAX_DOCUMENTS) {
      setError(`An envelope can hold at most ${MAX_DOCUMENTS} documents.`)
      return
    }
    setError('')
    setBusy(true)
    try {
      const loaded = await Promise.all(files.map(async (file) => ({
        upload: await uploadDocument(file),
        name: documentName(file),
        url: URL.createObjectURL(file),
      })))
      const at = docs.length
      setDocs((previous) => [...previous, ...loaded])
      // Shown straight away: somebody who has just added a document wants to
      // put boxes on it. Set directly rather than through goToDocument, which
      // bounds-checks against `docs` — this render's value, which does not
      // include what was just added.
      setDocIndex(at)
      setPage(1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Take a document out, and every box anybody had on it.
   *
   * The boxes on the documents AFTER it shift down by one, because the index is
   * the position in the list. Leaving them pointing at their old number would
   * move every box on the schedule onto the annex without anybody touching it.
   */
  function removeDocument(at: number) {
    if (docs.length <= 1) return
    URL.revokeObjectURL(docs[at].url)
    setDocs((previous) => previous.filter((_, i) => i !== at))
    setRecipients((rs) => rs.map((r) => ({
      ...r,
      fields: r.fields
        .filter((f) => f.documentIndex !== at)
        .map((f) => (f.documentIndex > at ? { ...f, documentIndex: f.documentIndex - 1 } : f)),
    })))
    const next = Math.max(0, at === 0 ? 0 : at - 1)
    setDocIndex(next)
    setPage(1)
    setSel({ r: firstSigningParty(recipients.map((r) => r.role)), f: 0 })
  }

  const signerCount = recipients.filter((r) => r.role === 'signer').length
  const triggerIdx = recipients.findIndex((r) => r.role === 'signer')
  const triggerFields = triggerIdx >= 0 ? recipients[triggerIdx].fields : []
  const hasCompareField = triggerFields.some((f) => f.kind === 'text')
  const textFieldIndices = triggerFields.flatMap((f, i) => (f.kind === 'text' ? [i] : []))
  const routingFieldIdx = textFieldIndices.includes(routingFieldIndex)
    ? routingFieldIndex
    : (textFieldIndices[0] ?? 0)
  const canRoute = order === 'sequential' && signerCount >= 2 && hasCompareField
  const routingHintText = routingHint({
    sequential: order === 'sequential', signers: signerCount, hasText: hasCompareField,
  })

  function buildRoutingRules(): RoutingRuleInput[] | undefined {
    if (!routingEnabled || !canRoute) return undefined
    const trigger = triggerIdx
    const branch = routingBranch(recipients.map((r) => r.role), trigger)
    if (branch.length === 0) return undefined
    const fieldIdx = routingFieldIdx
    const field = recipients[trigger]?.fields[fieldIdx]
    if (!field || field.kind !== 'text') return undefined
    const condition = {
      recipient_index: trigger,
      field_index: fieldIdx,
      op: 'gt' as const,
      value: routingThreshold.trim(),
    }
    if (branch.length === 1) {
      // Low value: skip the optional approver. High value: they must sign.
      return [{
        after_recipient_index: trigger,
        condition,
        if_true_skip: [],
        if_false_skip: [branch[0]],
      }]
    }
    return [{
      after_recipient_index: trigger,
      condition,
      if_true_skip: [branch[1]],
      if_false_skip: [branch[0]],
    }]
  }

  async function send() {
    if (!doc) return
    setError('')
    setBillingBlock(null)
    setBusy(true)
    try {
      const routingRules = buildRoutingRules()
      const orders = routingOrders(
        recipients.map((r) => r.role),
        order === 'sequential',
        routingRules?.[0]?.after_recipient_index ?? null,
      )
      const res = await createEnvelope({
        organization_id: orgID,
        title: title.trim(),
        document_key: docs[0].upload.document_key,
        extra_documents: docs.slice(1).map((d) => ({
          name: d.name,
          document_key: d.upload.document_key,
        })),
        retention_days: Math.round(retentionYears * 365),
        deliver_links: deliverLinks,
        reminders_enabled: deliverLinks ? remindersEnabled : undefined,
        routing_rules: routingRules,
        recipients: recipients.map((r, i) => {
          const base = {
            name: r.name.trim(),
            role: r.role,
            routing_order: orders[i],
            fields: signsDocument(r.role)
              ? r.fields.map((f) => {
                  const at = f.showWhen ? locateFieldKey(recipients, f.showWhen.fieldKey) : null
                  return {
                    kind: f.kind,
                    document_index: f.documentIndex,
                    page: f.page,
                    rect: f.rect,
                    required: f.required,
                    label: specFor(f.kind).labelled ? signerFieldPrompt(f.label, f.kind) : undefined,
                    options: kindNeedsOptions(f.kind)
                      ? f.options.map((o) => o.trim()).filter(Boolean)
                      : undefined,
                    show_when: at && f.showWhen
                      ? {
                          recipient_index: at.ri,
                          field_index: at.fi,
                          op: f.showWhen.op,
                          value: f.showWhen.value,
                        }
                      : undefined,
                  }
                })
              : [],
          }
          if (r.isGroup && r.members) {
            return {
              ...base,
              members: r.members.map((m) => ({
                name: m.name.trim(),
                email: m.email.trim(),
              })),
            }
          }
          return { ...base, email: r.email.trim() }
        }),
      })
      const ruleSent = routingRules?.[0]
      setSent({
        id: res.envelope.id,
        recipients: res.recipients,
        sequential: order === 'sequential',
        turns: {
          rows: recipients.map((r) => ({ name: r.name.trim(), role: r.role })),
          rowOf: sentRowOf(recipients.map((r) => ({ members: r.isGroup ? r.members : undefined }))),
          orders,
          rule: ruleSent
            ? {
                after: ruleSent.after_recipient_index,
                branch: routingBranch(recipients.map((r) => r.role), ruleSent.after_recipient_index),
              }
            : null,
        },
      })
      setStep('sent')
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'subscription_required' || e.code === 'plan_limit_exceeded')) {
        setBillingBlock(e.code)
        setError('')
        return
      }
      setError(e instanceof Error ? e.message : 'Could not send that document.')
    } finally {
      setBusy(false)
    }
  }

  async function copy(url: string, key: string) {
    await navigator.clipboard.writeText(url)
    setCopied(key)
    setTimeout(() => setCopied(''), 1800)
  }

  const everyoneNamed = recipients.every(draftComplete)
  const recipientSlots = participationCount(recipients)
  const hasSigner = recipients.some((r) => r.role === 'signer')
  // Everything the server refuses about a party's boxes, checked here so the
  // send button is off until it is fixed rather than the sender pressing it and
  // being told.
  // A box shown only when a box of somebody who does not sign is answered:
  // they are sent with no boxes, and the server refuses the send.
  // A rule with no answer, one waiting on a choice its box no longer offers,
  // and boxes that wait on each other: the server refuses the first and the
  // last, and the second hides its box for good (review-3-app request 1).
  // A box waiting on another person's box, which the server refuses too
  // (review 4, docs-1). A radio or dropdown whose options the server refuses
  // (review 4, app request 3).
  const layoutIsSendable = recipients.every(
    (r) => !signsDocument(r.role) || whatIsMissing(r.fields) === null,
  ) && showWhenOnNonSigners(recipients).length === 0
    && showWhenUnanswerable(recipients).length === 0
    && optionsRefused(recipients).length === 0
  // The same for the routing rule's threshold, which the server reads as a
  // number or refuses.
  const rulesToSend = buildRoutingRules()
  const thresholdUnreadable = rulesToSend !== undefined && !isRoutingThreshold(routingThreshold)
  // The roster says each signer's turn as it would be sent, so the two a rule
  // chooses between share one, as they will. Which of them signs is the
  // routing card's to say, under the roster.
  const rosterTurns = sentTurnWords(
    recipients.map((r) => ({ name: r.name, role: r.role })),
    routingOrders(
      recipients.map((r) => r.role), order === 'sequential', rulesToSend?.[0]?.after_recipient_index ?? null,
    ),
    null,
  )
  const canSend =
    Boolean(title.trim() && orgID && everyoneNamed && hasSigner && layoutIsSendable && !thresholdUnreadable)
    && !busy

  const simpleSidebar = fitsSimpleSidebar(recipients, routingEnabled)
  const suggestWorkflow = needsWorkflowStep(recipients, routingEnabled)

  function envelopeRecipients(): EnvelopeRecipient[] {
    return recipients.map((r) => ({
      key: r.key,
      name: r.name,
      email: r.email,
      role: r.role,
      isGroup: r.isGroup,
      members: r.members?.map((m) => ({ key: m.key, name: m.name, email: m.email })),
    }))
  }

  function openWorkflowBuilder() {
    const triggerIdx = recipients.findIndex((r) => r.role === 'signer')
    const branchTarget = recipients.find((r, i) => i > triggerIdx && r.role === 'signer')
    let draft = draftFromRecipients(
      envelopeRecipients(),
      title.trim() || docs[0]?.name || 'Document',
      (i) => i % WORKFLOW_TONES.length,
    )
    if (routingEnabled && triggerIdx >= 0 && branchTarget) {
      draft = draftWithRouting(
        draft,
        true,
        routingThreshold,
        recipients[triggerIdx].key,
        branchTarget.key,
      )
    }
    setWorkflowDraft(draft)
    setStep('workflow')
  }

  function applyWorkflowAndContinue() {
    if (!workflowDraft) return
    const { recipients: next, routing } = applyWorkflowDraft(
      workflowDraft,
      recipients,
      (p, members) => {
        if (members) {
          const row = newGroupDraft(recipients.length, page, p.role as RecipientRole, docIndex)
          return {
            ...row,
            name: p.name,
            members: members.map((m) => ({
              key: m.id,
              name: m.name,
              email: m.email,
            })),
          }
        }
        const prior = recipients.find((r) => r.key === p.id)
        if (prior) {
          return { ...prior, name: p.name, email: p.email, role: p.role }
        }
        return {
          ...newDraft(recipients.length, page, p.role as RecipientRole, docIndex),
          key: p.id,
          name: p.name,
          email: p.email,
          role: p.role as RecipientRole,
        }
      },
    )
    setRecipients(next)
    setOrder(routing.order)
    setRoutingEnabled(routing.routingEnabled)
    setRoutingThreshold(routing.routingThreshold)
    if (routing.routingEnabled) {
      const triggerIdx = next.findIndex((r) => r.role === 'signer')
      if (triggerIdx >= 0) {
        const want = routing.routingFieldLabel.trim().toLowerCase()
        const fields = next[triggerIdx].fields
        let textIdx = fields.findIndex(
          (f) => f.kind === 'text' && f.label.trim().toLowerCase() === want,
        )
        if (textIdx < 0) {
          textIdx = fields.findIndex(
            (f) => f.kind === 'text' && f.label.trim().toLowerCase().includes(want),
          )
        }
        if (textIdx < 0) textIdx = fields.findIndex((f) => f.kind === 'text')
        if (textIdx >= 0) setRoutingFieldIndex(textIdx)
      }
    }
    setUsedWorkflow(true)
    setSel({ r: firstSigningParty(next.map((r) => r.role)), f: 0 })
    setStep('place')
  }

  // A suggestion stays until somebody's box is on it or the sender dismisses
  // it, so the list is also the list of places nothing has been put yet.
  // Matched over every place in the documents, not only the open ones, so a
  // block keeps its person after its signature line has been taken.
  const candidates = envelopeCandidates(docs, readings)
  const coveredBy = candidates.map((c) => {
    const ri = recipients.findIndex((r) => signsDocument(r.role) && isCovered(c, r.fields))
    return ri < 0 ? null : ri
  })
  const matched = assignCandidates(candidates, recipients, coveredBy)
  const open = candidates.flatMap((c, i) => (coveredBy[i] === null && !dismissed.has(c.id) ? [i] : []))
  const suggestions = open.map((i) => candidates[i])
  const guessed = open.map((i) => matched[i])
  const signingParties = recipients.flatMap((r, ri) => (signsDocument(r.role) ? [ri] : []))

  /**
   * Who a suggestion goes to: the sender's pick, then the guess, then —
   * only for one box at a time — whoever the sender is working on.
   *
   * Never that last one for a signature. A signature line nobody could be
   * matched to is usually one more line than there are people, and giving it
   * to whoever is selected would move their signature off the line that was
   * theirs.
   */
  function ownerOf(i: number, fallback = true): number | null {
    const c = suggestions[i]
    const chosen = recipients.findIndex((r) => r.key === chosenOwner[c.id])
    if (signingParties.includes(chosen)) return chosen
    if (guessed[i] !== null) return guessed[i]
    if (!fallback || c.kind === 'signature') return null
    return signingParties.includes(sel.r) ? sel.r : null
  }

  function acceptSuggestion(i: number) {
    const ri = ownerOf(i)
    if (ri === null) return
    const next = withSuggestion(recipients, suggestions[i], ri)
    setRecipients(next.rs)
    setSel({ r: ri, f: next.f })
  }

  /**
   * Every suggestion whose person is known. One nobody could be matched to is
   * left for the sender, because giving it to whoever happens to be selected is
   * a guess made on their behalf, many times over.
   */
  function acceptAllSuggestions() {
    let rs = recipients
    suggestions.forEach((c, i) => {
      const ri = ownerOf(i, false)
      if (ri !== null) rs = withSuggestion(rs, c, ri).rs
    })
    setRecipients(rs)
  }

  function dismissSuggestion(id: string) {
    setDismissed((s) => new Set(s).add(id))
  }

  function showSuggestion(c: Candidate) {
    if (c.documentIndex === docIndex) {
      goToPage(c.page)
    } else {
      setDocIndex(c.documentIndex)
      setPage(c.page)
    }
    // On a phone the list is a screen below the document, and turning a page
    // nobody can see looks like the tap did nothing.
    const el = documentArea.current
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' })
  }

  // The last look before sending. A signed document with a blank "Initial
  // here" is the one that gets disputed, and by then nothing can be added.
  function requestSend() {
    if (suggestions.length > 0) {
      setChecking(true)
      return
    }
    void send()
  }

  // ------------------------------------------------------------- upload step

  if (step === 'upload') {
    return (
      <main className="page page-narrow">
        <div className="page-head">
          <h1>Send a document</h1>
          <p>
            Upload a PDF, choose who signs it and what they fill in, and send the links.
            Several PDFs go together as one envelope, signed in one sitting.
          </p>
        </div>

        <div
          className={`dropzone${dragOver ? ' is-over' : ''}`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            void onFiles(pdfsIn(e.dataTransfer.files))
          }}
        >
          {busy ? (
            <><span className="spinner" /><span>Reading document…</span></>
          ) : (
            <>
              <strong>Drop a PDF here</strong>
              <span className="small">
                or click to choose files · up to 25 MB each · {MAX_DOCUMENTS} in one envelope
              </span>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            hidden
            onChange={(e) => {
              void onFiles(pdfsIn(e.target.files))
              // Cleared so choosing the same file twice fires change again.
              e.target.value = ''
            }}
          />
        </div>

        {error && <div className="notice notice-bad" style={{ marginTop: '1rem' }}>{error}</div>}
      </main>
    )
  }

  // ----------------------------------------------------------- workflow step

  if (step === 'workflow' && workflowDraft) {
    return (
      <main className="page">
        <div className="page-head">
          <h1>Design the workflow</h1>
          <p>
            Set who signs, in what order, and any branches, then place fields on the document.
            {simpleSidebar && ' This envelope could also use the simple list on the next screen.'}
          </p>
        </div>
        <WorkflowBuilder
          draft={workflowDraft}
          onDraftChange={setWorkflowDraft}
          onContinue={applyWorkflowAndContinue}
          onUseSimpleList={simpleSidebar ? () => {
            setUsedWorkflow(false)
            setStep('place')
          } : undefined}
        />
        <p className="small faint" style={{ marginTop: '.75rem' }}>
          Branches need a text field on the prior signer. Add it when you place fields.
        </p>
      </main>
    )
  }

  // --------------------------------------------------------------- sent step

  if (step === 'sent' && sent) {
    return (
      <ReadyToSign
        sent={sent}
        copied={copied}
        onCopy={(url, key) => void copy(url, key)}
        onViewEnvelope={() => navigate(`/envelopes/${sent.id}`)}
      />
    )
  }

  // -------------------------------------------------------------- place step

  const current = recipients[sel.r]
  const currentField = current?.fields[sel.f]
  // Boxes are drawn only for the parties who sign. A draft may still hold the
  // ones a sender placed before changing somebody to a copy recipient — kept so
  // changing back restores them — and drawing those would put a signature box
  // on the page for a person who will never be asked for one.
  // How many boxes exist at all. The instruction below offers "click any other
  // box" only once there IS another one: on a fresh upload there is exactly
  // one, and telling somebody how to switch between boxes they do not have is
  // noise at the moment they can least afford it.
  const boxCount = recipients.reduce((n, r) => n + r.fields.length, 0)
  // A box shows only on the page of the document it is on. Filtering by page
  // alone would draw the schedule's boxes over the contract.
  const onThisPage = recipients.flatMap((r, ri) =>
    signsDocument(r.role)
      ? r.fields
          .map((f, fi) => ({ ri, f, fi }))
          .filter(({ f }) => f.page === page && f.documentIndex === docIndex)
      : [],
  )
  const documentNames = docs.map((d, i) => (i === 0 ? title.trim() || d.name : d.name))
  const linesHere = (readings[docs[docIndex]?.upload.document_key ?? '']?.lines ?? [])
    .filter((l) => l.page === page)
  const whereIs = (c: Candidate) => (docs.length > 1
    ? `${documentNames[c.documentIndex] ?? 'document'}, page ${c.page}`
    : `page ${c.page}`)
  const knownOwners = suggestions.filter((_, i) => ownerOf(i, false) !== null).length
  // Every party whose boxes the server would refuse, with what is wrong. Said
  // on the page, beside the send button that is off because of it.
  const unsendable = recipients
    .map((r, ri) => ({ r, ri, missing: signsDocument(r.role) ? whatIsMissing(r.fields) : null }))
    .filter((x) => x.missing !== null)

  return (
    <main className="page page-wide">
      <div className="page-head">
        <h1>Place the boxes</h1>
        <p>
          <PlaceHint
            name={current?.name.trim() ?? ''}
            index={sel.r}
            signs={!current || signsDocument(current.role)}
            field={currentField}
            here={currentField?.documentIndex === docIndex}
            documentName={currentField ? nameOfDocument(docs, currentField.documentIndex, title) : ''}
            more={boxCount > 1}
          />
        </p>
      </div>

      {billingBlock && orgID && (
        <div style={{ marginBottom: '1rem' }}>
          <SubscriptionPaywall orgId={orgID} code={billingBlock} />
        </div>
      )}

      {docs.length > 1 && (
        <nav className="doc-tabs" aria-label="Documents in this envelope">
          {docs.map((d, i) => (
            <button
              key={d.upload.document_key + String(i)}
              className={`doc-tab${i === docIndex ? ' is-current' : ''}`}
              aria-current={i === docIndex ? 'true' : undefined}
              onClick={() => goToDocument(i)}
            >
              <span className="doc-tab-n">{i + 1}</span>
              <span className="doc-tab-name">{i === 0 ? title.trim() || d.name : d.name}</span>
              <span className="small">{d.upload.page_count} pp</span>
            </button>
          ))}
        </nav>
      )}

      <div className="grid-doc">
        <div ref={documentArea}>
          <PdfView src={docUrl} page={page} onRendered={setSize} onError={setError}>
            {/* Drawn before the real boxes, so a box dragged across a
                suggestion stays on top of it and keeps the pointer. */}
            {suggestions.map((c, i) => {
              if (c.documentIndex !== docIndex || c.page !== page) return null
              const ri = ownerOf(i)
              const kind = kindLabel(c.kind)
              const who = ri === null ? null : recipients[ri].name.trim() || `Signer ${ri + 1}`
              // Measured on screen, not on the page: at phone width a box a
              // sixth of the page wide is too narrow for anybody's name.
              const roomy = c.rect.w * size.width >= 110
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`suggested-box${ri === null ? ' is-unmatched' : ''}`}
                  style={{
                    left: `${c.rect.x * 100}%`,
                    top: `${c.rect.y * 100}%`,
                    width: `${c.rect.w * 100}%`,
                    height: `${c.rect.h * 100}%`,
                    ...(ri === null ? {} : { '--field-tone': toneFor(ri) }),
                  } as React.CSSProperties}
                  aria-label={who === null
                    ? `Choose who the suggested ${kind.toLowerCase()} is for`
                    : `Add the suggested ${kind.toLowerCase()} for ${who}`}
                  onClick={() => {
                    if (ri !== null) acceptSuggestion(i)
                    else document.getElementById(`suggestion-owner-${c.id}`)?.focus()
                  }}
                >
                  <span className="suggested-box-label">
                    {roomy ? `+ ${kind} · ${who ?? 'choose who'}` : '+'}
                  </span>
                </button>
              )
            })}
            {onThisPage.map(({ ri, f, fi }) => (
              <FieldBox
                key={f.key}
                rect={f.rect}
                pageWidth={size.width}
                pageHeight={size.height}
                snap={(next) => snapToLines(next, linesHere, {
                  x: SNAP_PX.x / (size.width || 1),
                  y: SNAP_PX.y / (size.height || 1),
                })}
                onChange={(next) => patchField(ri, fi, { rect: next, placed: true })}
                label={boxLabel(recipients[ri].fields, fi, recipients[ri].name, ri)}
                tone={toneFor(ri)}
                muted={!(ri === sel.r && fi === sel.f)}
                onSelect={() => selectField(ri, fi)}
              />
            ))}
          </PdfView>

          {doc && doc.page_count > 1 && (
            <div className="page-nav">
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                ← Previous
              </button>
              <span>Page {page} of {doc.page_count}</span>
              <button
                className="btn btn-ghost"
                disabled={page >= doc.page_count}
                onClick={() => goToPage(page + 1)}
              >
                Next →
              </button>
            </div>
          )}

          {/* A box on another document, placed or not. The `placed` gate was
              here because an unplaced box used to follow the sender onto
              whatever they were looking at and so could never be elsewhere.
              It no longer follows across documents, so it can be — and a box
              nothing draws, nothing mentions and nothing can reach is still
              sent, at its default rectangle, on a document the sender never
              saw it on. */}
          {currentField && currentField.documentIndex !== docIndex && (
            <div className="notice" style={{ marginTop: '.75rem' }}>
              {/* The box is named, not described: "This initials is on…" is
                  what a sentence built from a kind label reads like, and two
                  of the seven kinds are plural nouns. */}
              The {kindLabel(currentField.kind).toLowerCase()} box is on{' '}
              {nameOfDocument(docs, currentField.documentIndex, title)}.{' '}
              <button className="btn-link" onClick={() => goToDocument(currentField.documentIndex)}>
                Show that document
              </button>
              {' · '}
              <button
                className="btn-link"
                onClick={() => patchField(sel.r, sel.f, { documentIndex: docIndex, page, placed: true })}
              >
                Move it here
              </button>
            </div>
          )}

          {currentField && currentField.placed && currentField.documentIndex === docIndex
            && currentField.page !== page && (
            <div className="notice" style={{ marginTop: '.75rem' }}>
              The {kindLabel(currentField.kind).toLowerCase()} box sits on page {currentField.page}.{' '}
              <button className="btn-link" onClick={() => patchField(sel.r, sel.f, { page, placed: true })}>
                Move it to page {page}
              </button>
            </div>
          )}

          <div className="row" style={{ marginTop: '.75rem' }}>
            <button
              className="btn btn-ghost"
              disabled={busy || docs.length >= MAX_DOCUMENTS}
              onClick={() => moreInput.current?.click()}
            >
              Add another document
            </button>
            {docs.length > 1 && (
              <button className="btn btn-ghost" disabled={busy} onClick={() => removeDocument(docIndex)}>
                Remove this document
              </button>
            )}
            <input
              ref={moreInput}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              hidden
              onChange={(e) => {
                void addDocuments(pdfsIn(e.target.files))
                e.target.value = ''
              }}
            />
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <div style={{ marginBottom: '.9rem' }}>
              <label htmlFor="title">Document title</label>
              <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <SentBy session={session} organizationID={orgID} />
            <OrganizationPicker session={session} value={orgID} onChange={setOrgID} />
          </div>

          {usedWorkflow && (
            <div className="card card-tight">
              <div className="row-between" style={{ marginBottom: '.35rem' }}>
                <strong className="small">Workflow</strong>
                <button type="button" className="btn btn-ghost small" onClick={openWorkflowBuilder}>
                  Edit workflow
                </button>
              </div>
              <p className="small muted" style={{ margin: 0 }}>
                Routing was designed on the workflow canvas.
                {routingEnabled && ' A conditional branch runs after the first signer.'}
              </p>
            </div>
          )}

          {!usedWorkflow && (
            <div className="card card-tight">
              <p className="small muted" style={{ margin: '0 0 .5rem' }}>
                {suggestWorkflow
                  ? 'The workflow builder sets branches and groups.'
                  : 'The list sets who signs, and in what order.'}
              </p>
              <button type="button" className="btn btn-ghost btn-block small" onClick={openWorkflowBuilder}>
                Design in workflow builder
              </button>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="card suggestions">
              <div className="row-between">
                <strong>Found in the document</strong>
                <span className="small faint">{suggestions.length}</span>
              </div>
              <p className="small faint">
                Places the document asks for a signature, a date or an answer. Nothing is
                added until you add it.
              </p>
              <ul className="suggestion-list">
                {suggestions.map((c, i) => {
                  const ri = ownerOf(i)
                  const here = c.documentIndex === docIndex && c.page === page
                  return (
                    <li key={c.id} className={`suggestion-row${here ? ' is-active' : ''}`}>
                      <span
                        className="dot"
                        style={{ background: ri === null ? 'var(--text-faint)' : toneFor(ri) }}
                        aria-hidden
                      />
                      <button className="btn-link suggestion-where" onClick={() => showSuggestion(c)}>
                        {kindLabel(c.kind)} · {whereIs(c)}
                      </button>
                      <span className="row-tight">
                        <button
                          className="btn btn-secondary small"
                          disabled={ri === null}
                          onClick={() => acceptSuggestion(i)}
                        >
                          Add
                        </button>
                        <button
                          className="btn-icon"
                          aria-label={`Dismiss the ${kindLabel(c.kind).toLowerCase()} on ${whereIs(c)}`}
                          onClick={() => dismissSuggestion(c.id)}
                        >
                          ×
                        </button>
                      </span>
                      <span className="small faint suggestion-source">{c.source}</span>
                      {signingParties.length > 1 && (
                        <select
                          id={`suggestion-owner-${c.id}`}
                          className="suggestion-owner"
                          aria-label={`Who the ${kindLabel(c.kind).toLowerCase()} on ${whereIs(c)} is for`}
                          value={ri ?? ''}
                          onChange={(e) => {
                            const picked = recipients[Number(e.target.value)]
                            if (picked) setChosenOwner((m) => ({ ...m, [c.id]: picked.key }))
                          }}
                        >
                          {ri === null && <option value="">Choose who</option>}
                          {signingParties.map((p) => (
                            <option key={recipients[p].key} value={p}>
                              {recipients[p].name.trim() || `Signer ${p + 1}`}
                            </option>
                          ))}
                        </select>
                      )}
                    </li>
                  )
                })}
              </ul>
              {knownOwners > 1 && (
                <button className="btn btn-secondary btn-block" onClick={acceptAllSuggestions}>
                  {knownOwners === suggestions.length
                    ? `Add all ${knownOwners}`
                    : `Add the ${knownOwners} matched to someone`}
                </button>
              )}
            </div>
          )}

          <div className="card">
            <div className="row-between" style={{ marginBottom: '.75rem' }}>
              <strong>{usedWorkflow ? 'Fields per person' : 'Who signs'}</strong>
              <span className="small faint">{recipientSlots} of {MAX_PARTICIPANTS}</span>
            </div>

            <div className="stack-tight">
              {recipients.map((r, ri) => (
                <div
                  key={r.key}
                  className={`signer-row${ri === sel.r ? ' is-active' : ''}`}
                  onClick={() => { if (ri !== sel.r) selectField(ri, 0) }}
                >
                  <div className="row-between" style={{ marginBottom: '.4rem' }}>
                    <span className="small" style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                      <span className="dot" style={{ background: toneFor(ri) }} aria-hidden />
                      {r.isGroup
                        ? `${r.name.trim() || 'Signing group'} · any one of ${r.members?.length ?? 0}`
                        : order === 'sequential' && r.role === 'signer'
                          ? rosterTurns.labels[ri]
                          : ROLE_LABELS[r.role]}
                    </span>
                    <span className="row-tight">
                      {!usedWorkflow && order === 'sequential' && recipients.length > 1 && (
                        <>
                          <button className="btn-icon" aria-label="Move earlier" disabled={ri === 0}
                            onClick={(e) => { e.stopPropagation(); moveRecipient(ri, -1) }}>↑</button>
                          <button className="btn-icon" aria-label="Move later"
                            disabled={ri === recipients.length - 1}
                            onClick={(e) => { e.stopPropagation(); moveRecipient(ri, 1) }}>↓</button>
                        </>
                      )}
                      {recipients.length > 1 && (
                        <button className="btn-icon" aria-label={`Remove signer ${ri + 1}`}
                          onClick={(e) => { e.stopPropagation(); removeRecipient(ri) }}>×</button>
                      )}
                    </span>
                  </div>
                  {/* Stable ids so the browser smoke test has something
                      durable to hold onto as this list grows and reorders. */}
                  {r.isGroup ? (
                    <>
                      <input id={`signer-${ri}-name`}
                        type="text" placeholder="Group label, e.g. Directors" value={r.name}
                        onChange={(e) => patchRecipient(ri, { name: e.target.value })}
                        style={{ marginBottom: '.4rem' }} />
                      <div className="stack-tight" style={{ marginBottom: '.4rem' }}>
                        {r.members?.map((m, mi) => (
                          <div key={m.key} className="row-tight">
                            <input
                              type="text"
                              placeholder="Name"
                              value={m.name}
                              onChange={(e) => setRecipients((rs) => rs.map((row, i) => {
                                if (i !== ri || !row.members) return row
                                return {
                                  ...row,
                                  members: row.members.map((member, j) =>
                                    j === mi ? { ...member, name: e.target.value } : member),
                                }
                              }))}
                            />
                            <input
                              type="email"
                              placeholder="name@example.com"
                              value={m.email}
                              onChange={(e) => setRecipients((rs) => rs.map((row, i) => {
                                if (i !== ri || !row.members) return row
                                return {
                                  ...row,
                                  members: row.members.map((member, j) =>
                                    j === mi ? { ...member, email: e.target.value } : member),
                                }
                              }))}
                            />
                            {(r.members?.length ?? 0) > 2 && (
                              <button className="btn-icon" aria-label="Remove member"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setRecipients((rs) => rs.map((row, i) => {
                                    if (i !== ri || !row.members) return row
                                    return {
                                      ...row,
                                      members: row.members.filter((_, j) => j !== mi),
                                    }
                                  }))
                                }}>×</button>
                            )}
                          </div>
                        ))}
                        {(r.members?.length ?? 0) < MAX_GROUP_MEMBERS && recipientSlots < MAX_PARTICIPANTS && (
                          <button className="btn btn-ghost small" onClick={(e) => {
                            e.stopPropagation()
                            setRecipients((rs) => rs.map((row, i) => {
                              if (i !== ri) return row
                              return { ...row, members: [...(row.members ?? []), newMember()] }
                            }))
                          }}>+ Add member</button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <input id={`signer-${ri}-name`}
                        type="text" placeholder="Name" value={r.name}
                        onChange={(e) => patchRecipient(ri, { name: e.target.value })}
                        style={{ marginBottom: '.4rem' }} />
                      <input id={`signer-${ri}-email`}
                        type="email" placeholder="name@example.com" value={r.email}
                        onChange={(e) => patchRecipient(ri, { email: e.target.value })}
                        style={{ marginBottom: '.4rem' }} />
                    </>
                  )}

                  <div style={{ marginBottom: '.4rem' }} onClick={(e) => e.stopPropagation()}>
                    <label className="small faint" htmlFor={`signer-${ri}-role`}>Role</label>
                    <select
                      id={`signer-${ri}-role`}
                      value={r.role}
                      onChange={(e) => {
                        const role = e.target.value as RecipientRole
                        setRecipients((rs) => setRecipientRole(rs, ri, role, page, docIndex))
                        if (ri === sel.r) setSel({ r: ri, f: 0 })
                      }}
                    >
                      <option value="signer">Signer</option>
                      <option value="witness">Witness</option>
                      <option value="cc">Receives a copy</option>
                      <option value="needs_to_view">Needs to view</option>
                    </select>
                  </div>

                  {ri === sel.r && signsDocument(r.role) && (
                    <div className="field-list">
                      {r.fields.map((f, fi) => (
                        <div
                          key={f.key}
                          ref={fi === sel.f ? activeRow : undefined}
                          className={`field-row${fi === sel.f ? ' is-active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); selectField(ri, fi) }}
                        >
                          <div className="row-between">
                            <span className="small">{fieldDisplayName(r.fields, fi, documentNames)}</span>
                            {/* A signature box can go only when it is not this
                                party's last AND nothing else of theirs is on
                                that document. It is what their seal there is
                                anchored to, so removing it while their text
                                boxes remain would leave a layout the server
                                refuses — and a × that silently took four other
                                boxes with it is not a × anybody expects. */}
                            {canRemove(r.fields, fi) && (
                              <button className="btn-icon" aria-label={`Remove ${kindLabel(f.kind)}`}
                                onClick={(e) => { e.stopPropagation(); removeField(ri, fi) }}>×</button>
                            )}
                          </div>

                          {fi === sel.f && specFor(f.kind).labelled && (
                            <>
                              <label className="small faint" htmlFor={`field-prompt-${f.key}`} style={{ marginTop: '.4rem', display: 'block' }}>
                                What the signer sees
                              </label>
                              <input
                                id={`field-prompt-${f.key}`}
                                type="text"
                                placeholder={
                                  f.kind === 'checkbox' ? 'What they are agreeing to'
                                    : kindNeedsOptions(f.kind) ? 'What are they choosing?'
                                      : 'e.g. Amount, Job number'
                                }
                                value={f.label}
                                onChange={(e) => patchField(ri, fi, { label: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </>
                          )}
                          {fi === sel.f && kindNeedsOptions(f.kind) && (
                            <div className="option-list" style={{ marginTop: '.4rem' }}>
                              {f.options.map((opt, oi) => (
                                <div key={oi} className="option-row">
                                  <input
                                    type="text"
                                    aria-label={`Option ${oi + 1}`}
                                    value={opt}
                                    onChange={(e) => {
                                      const next = [...f.options]
                                      next[oi] = e.target.value
                                      patchField(ri, fi, { options: next })
                                    }}
                                  />
                                  {f.options.length > 2 && (
                                    <button
                                      type="button"
                                      className="btn-icon"
                                      aria-label={`Remove option ${oi + 1}`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        patchField(ri, fi, { options: f.options.filter((_, j) => j !== oi) })
                                      }}
                                    >×</button>
                                  )}
                                </div>
                              ))}
                              {f.options.length < 20 && (
                                <button
                                  type="button"
                                  className="btn btn-ghost small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    patchField(ri, fi, { options: [...f.options, `Option ${f.options.length + 1}`] })
                                  }}
                                >
                                  + Option
                                </button>
                              )}
                            </div>
                          )}
                          {fi === sel.f && f.kind !== 'signature' && f.kind !== 'date_signed' && (
                            <label className="check small" style={{ marginTop: '.4rem' }}>
                              <input
                                type="checkbox"
                                checked={f.required}
                                onChange={(e) => patchField(ri, fi, { required: e.target.checked })}
                              />
                              Required
                            </label>
                          )}
                          {fi === sel.f && f.kind !== 'signature' && f.kind !== 'date_signed' && (
                            <ShowWhenPicker
                              recipients={recipients}
                              fieldKey={f.key}
                              value={f.showWhen}
                              onChange={(showWhen) => patchField(ri, fi, { showWhen })}
                            />
                          )}
                        </div>
                      ))}

                      {/* A party needs a signature box on each document they
                          fill anything in on, so wherever they have none on the
                          document being looked at, there is a way to add one.
                          Offered with a single document too: removing a
                          document takes its boxes with it, and a party whose
                          only boxes were there is left with nothing and no
                          control that can give them any. */}
                      {!r.fields.some(
                        (f) => f.kind === 'signature' && f.documentIndex === docIndex,
                      ) && (
                        <button
                          className="btn btn-ghost small"
                          onClick={(e) => { e.stopPropagation(); addField('signature') }}
                        >
                          {docs.length > 1
                            ? `+ Signature on ${nameOfDocument(docs, docIndex, title)}`
                            : '+ Signature'}
                        </button>
                      )}

                      {supportsFields ? (
                        <div className="palette">
                          {KINDS.filter((k) => k.kind !== 'signature').map((k) => (
                            <button
                              key={k.kind}
                              className="btn btn-ghost small"
                              title={k.hint}
                              onClick={(e) => { e.stopPropagation(); addField(k.kind) }}
                            >
                              + {k.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="small faint" style={{ marginTop: '.5rem', marginBottom: 0 }}>
                          This PDF is too old to carry anything but a signature. Re-save it from a
                          current PDF tool to add dates, initials or text.
                        </p>
                      )}
                    </div>
                  )}

                  {ri === sel.r && r.role === 'cc' && (
                    <p className="small faint" style={{ margin: 0 }}>
                      Gets a copy when the envelope is finished. No boxes to place.
                    </p>
                  )}

                  {ri === sel.r && r.role === 'needs_to_view' && (
                    <p className="small faint" style={{ margin: 0 }}>
                      Must open the document before the envelope can finish. No boxes to place.
                    </p>
                  )}
                </div>
              ))}
            </div>

            {!usedWorkflow && (
              <RosterActions
                slots={recipientSlots}
                onAddSigner={addRecipient}
                onAddGroup={() => {
                  const r = recipients.length
                  setRecipients((rs) => [...rs, newGroupDraft(r, page, 'signer', docIndex)])
                  setSel({ r, f: 0 })
                }}
              />
            )}

            {!usedWorkflow && recipients.length > 1 && (
              <div style={{ marginTop: '.9rem' }}>
                <label htmlFor="order">Signing order</label>
                <select id="order" value={order} onChange={(e) => setOrder(e.target.value as Order)}>
                  <option value="sequential">One after another, in this order</option>
                  <option value="parallel">Everyone at once</option>
                </select>
                <p className="small faint" style={{ marginTop: '.4rem', marginBottom: 0 }}>
                  {order === 'sequential'
                    ? 'Each person can only sign once everyone above them has. They sign the copy carrying the earlier signatures.'
                    : 'Everyone can sign as soon as they get their link. The document is finished when the last one signs.'}
                </p>
              </div>
            )}

            {!usedWorkflow && recipients.length > 1 && order === 'sequential' && (
              <div style={{ marginTop: '.9rem' }}>
                <strong className="small">Routing rules</strong>
                {routingLost && (
                  <div className="notice notice-warn small" id="routing-lost" style={{ marginTop: '.4rem' }}>
                    The document this came from had a routing rule that cannot be set here. Set one again, or it is
                    sent without one.
                  </div>
                )}
                {routingHintText && (
                  <p className="small faint" style={{ margin: '.4rem 0 0' }}>{routingHintText}</p>
                )}
                <label className="check" style={{ marginTop: '.6rem' }}>
                  <input
                    type="checkbox"
                    checked={routingEnabled}
                    disabled={!canRoute}
                    onChange={(e) => setRoutingEnabled(e.target.checked)}
                  />
                  Route signers by a number they enter
                </label>
                {routingEnabled && canRoute && (
                  <div className="stack-tight" style={{ marginTop: '.6rem' }}>
                    <p className="small faint" style={{ margin: 0 }}>
                      After {recipients[triggerIdx]?.name.trim() || 'the first signer'} signs, the number they entered is checked.
                    </p>
                    <label className="small faint" htmlFor="routing-threshold">When the amount is over</label>
                    <input
                      id="routing-threshold"
                      type="text"
                      aria-invalid={thresholdUnreadable || undefined}
                      aria-describedby={thresholdUnreadable ? 'routing-threshold-problem' : undefined}
                      value={routingThreshold}
                      onChange={(e) => setRoutingThreshold(e.target.value)}
                    />
                    {/* Under the field it is about, and read with it. It was
                        a notice beside the send button, about 650px away. */}
                    {thresholdUnreadable && (
                      <p className="small field-problem" id="routing-threshold-problem">
                        The routing rule needs a number to compare the answer with, such as 50000.
                      </p>
                    )}
                    <label className="small faint" htmlFor="routing-field">Which field</label>
                    <select
                      id="routing-field"
                      value={routingFieldIdx}
                      onChange={(e) => setRoutingFieldIndex(Number(e.target.value))}
                    >
                      {triggerFields.map((f, fi) => (
                        f.kind === 'text' ? (
                          <option key={f.key} value={fi}>
                            {fieldDisplayName(triggerFields, fi)}
                          </option>
                        ) : null
                      ))}
                    </select>
                    <p className="small faint" id="routing-outcome" style={{ margin: 0 }}>
                      {routingOutcome(recipients.map((r) => r.name), routingBranch(recipients.map((r) => r.role), triggerIdx))}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card">
            <div className="check-explained check-explained-block">
              <input
                id="deliver-links"
                type="checkbox"
                checked={deliverLinks}
                onChange={(e) => setDeliverLinks(e.target.checked)}
                aria-describedby="deliver-links-why"
              />
              <label htmlFor="deliver-links" style={{ marginBottom: 0 }}>
                Email each link from docsdone
              </label>
              <div id="deliver-links-why" className="small faint why">
                docsdone emails each person and records that the message was sent.
              </div>
            </div>
            {deliverLinks && (
              <div className="check-explained check-explained-block">
                <input
                  id="reminders-enabled"
                  type="checkbox"
                  checked={remindersEnabled}
                  onChange={(e) => setRemindersEnabled(e.target.checked)}
                  aria-describedby="reminders-enabled-why"
                />
                <label htmlFor="reminders-enabled" style={{ marginBottom: 0 }}>
                  Remind them automatically
                </label>
                <div id="reminders-enabled-why" className="small faint why">
                  Sent 1, 3, and 7 days after the link, to anyone who has not signed and whose turn it is.
                </div>
              </div>
            )}

            <KeepFor maxDays={maxRetentionDays} chosen={chosenYears} onChoose={setChosenYears} />
          </div>

          {docs.length > 0 && (
            <div className="card card-tight small muted">
              {docs.length > 1 && (
                <div className="row-between"><span>Documents</span><span>{docs.length}</span></div>
              )}
              <div className="row-between">
                <span>Pages</span>
                <span>{docs.reduce((n, d) => n + d.upload.page_count, 0)}</span>
              </div>
              <div className="row-between">
                <span>Size</span>
                <span>{bytes(docs.reduce((n, d) => n + d.upload.bytes, 0))}</span>
              </div>
              <div className="row-between">
                <span>Boxes</span>
                <span>{boxCount}</span>
              </div>
            </div>
          )}

          {unsendable.map(({ r, ri, missing }) => (
            <div key={r.key} className="notice notice-bad small">
              {missing?.kind === 'nothing-to-sign' && (
                <>
                  {r.name.trim() || `Signer ${ri + 1}`} has nothing to sign. Add a signature
                  box for them, or change their role to one that does not sign.
                </>
              )}
              {missing?.kind === 'unsigned-document' && (
                <>
                  {r.name.trim() || `Signer ${ri + 1}`} has boxes on{' '}
                  {nameOfDocument(docs, missing.documentIndex, title)} with nothing to
                  sign there. Add a signature box, so what they fill in is covered by their
                  signature.
                </>
              )}
              {missing?.kind === 'two-signatures' && (
                <>
                  {r.name.trim() || `Signer ${ri + 1}`} has two signature boxes on{' '}
                  {nameOfDocument(docs, missing.documentIndex, title)}. Their signature goes
                  in one place on each document; remove one.
                </>
              )}
            </div>
          ))}

          {/* One notice per box, naming the box and its document, with a way
              to it: two such boxes gave two identical notices that named
              neither (review-3 design-3). */}
          {showWhenOnNonSigners(recipients).map((x) => {
            const ri = recipients.findIndex((r) => r.key === x.recipientKey)
            const fi = recipients[ri]?.fields.findIndex((f) => f.key === x.fieldKey) ?? -1
            const field = recipients[ri]?.fields[fi]
            return (
              <div key={`show-when-${x.fieldKey}`} className="notice notice-bad small">
                {showWhenOnNonSignerWords(recipients[ri]?.name ?? '', ri, recipients[x.controllerIndex]?.name ?? '', x.controllerIndex,
                  field && { prompt: signerFieldPrompt(field.label, field.kind), document: nameOfDocument(docs, x.documentIndex, title) })}{' '}
                {field && (
                  <button className="btn-link" type="button" onClick={() => showBox(ri, fi)}>
                    Show the box
                  </button>
                )}
              </div>
            )
          })}

          {/* Rules that can never be met, each named as the notice above
              names its box (review-3-app request 1). */}
          {showWhenUnanswerable(recipients).map((x) => {
            const ri = recipients.findIndex((r) => r.key === x.recipientKey)
            const fi = recipients[ri]?.fields.findIndex((f) => f.key === x.fieldKey) ?? -1
            const field = recipients[ri]?.fields[fi]
            return (
              <div key={`show-when-fault-${x.fieldKey}`} className="notice notice-bad small">
                {showWhenFaultWords(x.fault, recipients[ri]?.name ?? '', ri, recipients[x.controllerIndex]?.name ?? '', x.controllerIndex,
                  field?.showWhen?.value?.trim() ?? '',
                  field && { prompt: signerFieldPrompt(field.label, field.kind), document: nameOfDocument(docs, x.documentIndex, title) })}{' '}
                {field && (
                  <button className="btn-link" type="button" onClick={() => showBox(ri, fi)}>
                    Show the box
                  </button>
                )}
              </div>
            )
          })}

          {/* Options the server refuses, named as the notices above name
              their box (review 4, app request 3). */}
          {optionsRefused(recipients).map((x) => {
            const ri = recipients.findIndex((r) => r.key === x.recipientKey)
            const fi = recipients[ri]?.fields.findIndex((f) => f.key === x.fieldKey) ?? -1
            const field = recipients[ri]?.fields[fi]
            return (
              <div key={`options-${x.fieldKey}`} className="notice notice-bad small">
                {optionsFaultWords(x.fault, recipients[ri]?.name ?? '', ri, x.repeated ?? '',
                  field && { prompt: signerFieldPrompt(field.label, field.kind), document: nameOfDocument(docs, x.documentIndex, title) })}{' '}
                {field && (
                  <button className="btn-link" type="button" onClick={() => showBox(ri, fi, { part: 'options', fault: x.fault })}>
                    Show the box
                  </button>
                )}
              </div>
            )
          })}

          {error &&<div className="notice notice-bad">{error}</div>}

          {checking && (
            <ConfirmSheet
              title="Review before sending"
              confirmLabel="Send anyway"
              cancelLabel="Review them"
              recommend="cancel"
              onConfirm={() => {
                setChecking(false)
                void send()
              }}
              onClose={() => {
                setChecking(false)
                if (suggestions[0]) showSuggestion(suggestions[0])
              }}
            >
              <p>
                {suggestions.length === 1
                  ? 'The following field has no input defined.'
                  : 'The following fields have no inputs defined.'}{' '}
                Nothing can be added once the document is signed.
              </p>
              <ul className="suggestion-missed">
                {suggestions.slice(0, 5).map((c) => (
                  <li key={c.id}>
                    <strong>{kindLabel(c.kind)}</strong> · {whereIs(c)}
                    <span className="small faint"> · {c.source}</span>
                  </li>
                ))}
              </ul>
              {suggestions.length > 5 && (
                <p className="small faint">And {suggestions.length - 5} more.</p>
              )}
            </ConfirmSheet>
          )}

          <button className="btn btn-lg" disabled={!canSend} onClick={requestSend}>
            {busy ? <><span className="spinner" /> Sending…</> : `Create ${signingLinkCount(recipients.map((r) => ({ role: r.role, members: r.isGroup ? r.members : undefined }))) === 1 ? 'signing link' : 'signing links'}`}
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => {
              for (const d of docs) URL.revokeObjectURL(d.url)
              setDocs([])
              setDocIndex(0)
              setStep('upload')
              setError('')
            }}
          >
            Start again with different files
          </button>
        </div>
      </div>
    </main>
  )
}

/**
 * An envelope just sent: the server's answer, and what was sent with it, so
 * the page can say whose turn is which from the routing orders and the rule
 * that went out rather than from positions in a list.
 */
export interface SentEnvelope {
  id: string
  recipients: CreatedRecipient[]
  sequential: boolean
  turns?: { rows: SentRow[]; rowOf: number[]; orders: number[]; rule: SentRule | null }
}

/**
 * The page after sending: each person's link, or where docsdone sent it.
 *
 * It said "In production these are emailed to each recipient rather than
 * shown here." under every send. That stopped being true when emailing became
 * the sender's choice: a link is shown here exactly when the sender chose to
 * hand it on, and a link docsdone emailed is not shown at all. What is true of
 * a link that is shown is the warning that went with it, now said only when
 * there is one to keep safe.
 */
export function ReadyToSign({
  sent,
  copied,
  onCopy,
  onViewEnvelope,
}: {
  sent: SentEnvelope
  copied: string
  onCopy: (url: string, key: string) => void
  onViewEnvelope: () => void
}) {
  const sequential = sent.sequential
  const first = sent.recipients[0]
  const linksShown = sent.recipients.some((r) => r.role !== 'cc' && r.signing_url)
  // Without what was sent (a server's answer alone), every recipient is a row
  // of their own and takes the next turn.
  const turns = sent.turns ?? {
    rows: sent.recipients.map((r) => ({ name: r.name, role: r.role })),
    rowOf: sent.recipients.map((_, i) => i),
    orders: sent.recipients.map((_, i) => i + 1),
    rule: null,
  }
  const words = sentTurnWords(turns.rows, turns.orders, turns.rule)
  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Ready to sign</h1>
        <p>
          {sent.recipients.length === 1
            ? first.signing_url
              ? `Send this link to ${first.name}. It expires in 30 days.`
              : first.delivered === false
                ? `The link for ${first.name} could not be emailed.`
                : `docsdone emailed ${first.name} their link. It expires in 30 days.`
            : sequential
              ? words.lead
              : 'Each person gets their own link and they can sign in any order.'}
        </p>
      </div>

      <div className="stack">
        {sent.recipients.map((r, i) => (
          <div className="card" key={r.participation_id}>
            <div className="row-between" style={{ marginBottom: '.6rem' }}>
              <label htmlFor={`link-${r.participation_id}`} style={{ marginBottom: 0 }}>
                <span className="dot" style={{ background: toneFor(i) }} aria-hidden />
                {r.name} <span className="muted small">&lt;{r.email}&gt;</span>
              </label>
              {sequential && r.role === 'signer' && (
                <span className="small faint">{words.labels[turns.rowOf[i] ?? i]}</span>
              )}
              {(r.role !== 'signer' || !sequential) && r.role !== 'cc' && (
                <span className="small faint">{ROLE_LABELS[r.role]}</span>
              )}
            </div>
            {r.role === 'cc' ? (
              <p className="small muted" style={{ margin: 0 }}>
                Notified with a copy when everyone has finished.
              </p>
            ) : r.signing_url ? (
              <div className="copy-field">
                <input
                  id={`link-${r.participation_id}`}
                  type="text"
                  readOnly
                  value={r.signing_url}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => onCopy(r.signing_url!, r.participation_id)}
                >
                  {copied === r.participation_id ? 'Copied' : 'Copy'}
                </button>
              </div>
            ) : r.delivered === false ? (
              // The send was refused. Saying "Sent to …" here would be the
              // one falsehood this page can tell: the signer has no link, and
              // a sender who believes otherwise waits for a signature that
              // cannot arrive.
              <p className="small" style={{ margin: 0, color: 'var(--bad)' }}>
                Could not be delivered to {r.sent_to ?? r.email}. Reissue the link from the
                envelope to try again.
              </p>
            ) : (
              <p className="small muted" style={{ margin: 0 }}>Sent to {r.sent_to ?? r.email}.</p>
            )}
          </div>
        ))}
      </div>

      {linksShown && (
        <p className="small muted" style={{ marginTop: '.9rem' }}>
          Anyone with a link can sign as that person.
        </p>
      )}

      <div className="row" style={{ marginTop: '1rem' }}>
        {first.signing_url && (
          <a className="btn" href={first.signing_url}>Open the first signing page</a>
        )}
        <button className="btn btn-secondary" onClick={onViewEnvelope}>
          View envelope
        </button>
      </div>
    </main>
  )
}

/**
 * The line under "Place the boxes", for the party being worked on. A party
 * can have no boxes at all (removing a document takes its boxes with it), and
 * telling somebody to drag a box they do not have is worse than saying
 * nothing. A party who does not sign has none to place, whatever it kept from
 * before its role changed: Send again opened on a viewer and told the sender
 * to add a signature for them (L18).
 */
export function PlaceHint({ name, index, signs, field, here, documentName, more }: {
  name: string
  index: number
  signs: boolean
  field?: { kind: FieldKind }
  here: boolean
  documentName: string
  more: boolean
}) {
  const who = name || `Signer ${index + 1}`
  // Not "Signer 1": a party who does not sign is not one, and an unnamed
  // viewer read "Signer 1 does not sign" (D5).
  if (!signs) return <>{name || 'This person'} does not sign, so they have no boxes.</>
  if (field && here) {
    return (
      <>
        Drag the {kindLabel(field.kind).toLowerCase()} for {name || `signer ${index + 1}`}.
        {more && ' Click any other box to work on that one.'}
      </>
    )
  }
  if (field) return <>{who}'s {kindLabel(field.kind).toLowerCase()} is on {documentName}.</>
  return <>{who} has no boxes. Add a signature for them from the panel.</>
}

/**
 * The two ways to add a party, stacked rather than side by side. Two
 * .btn-block buttons sharing an inline-flex row each got half the panel, and
 * "+ Add another signer" wrapped to two lines at every width photographed, 1440
 * included. The panel is 380px at its widest and these labels do not fit
 * beside each other in it.
 *
 * A signing group starts with two members, and each member is one of the
 * twenty participations an envelope may carry, so it is offered only while
 * two more fit. Both used to be offered while one place was left, so the
 * group took the envelope to twenty-one, which the server refuses.
 */
export function RosterActions({
  slots,
  onAddSigner,
  onAddGroup,
}: {
  slots: number
  onAddSigner: () => void
  onAddGroup: () => void
}) {
  if (slots >= MAX_PARTICIPANTS) return null
  return (
    <div className="roster-actions">
      <button className="btn btn-ghost btn-block" onClick={onAddSigner}>
        + Add another signer
      </button>
      {slots + 2 <= MAX_PARTICIPANTS && (
        <button className="btn btn-ghost btn-block" onClick={onAddGroup}>
          + Add signing group
        </button>
      )}
    </div>
  )
}

/** What a box says on the page: whose it is, and what it is for. */
function boxLabel(fields: DraftField[], fi: number, name: string, index: number): string {
  const f = fields[fi]
  const who = name.trim() || `Signer ${index + 1}`
  if (f.kind === 'signature') return who
  return `${who} · ${signerFieldPrompt(f.label, f.kind)}`
}

/**
 * Why a box shown only when a non-signer's box is answered stops the send, in
 * the app's words (mobile/src/features/compose/send/words.ts). Given the box,
 * the sentence names it and its document: the app selects the box for the
 * sender, and the web's notices otherwise read word for word the same for
 * two boxes (review-3 design-3).
 */
export function showWhenOnNonSignerWords(
  name: string,
  index: number,
  controllerName: string,
  controllerIndex: number,
  box?: { prompt: string; document: string },
): string {
  const who = name.trim() || `Signer ${index + 1}`
  // Not "Signer 2 does not sign" for a viewer with no name yet.
  const controller = controllerName.trim() || `Person ${controllerIndex + 1}`
  const rest = `${controller} does not sign and answers nothing. Under “Show only when”, choose another box or “Always shown”.`
  if (box) {
    return `${who}'s “${box.prompt}” box on ${box.document} is shown only when ${controller} answers one of their boxes, but ${rest}`
  }
  return `${who} has a box shown only when ${controller} answers one of their boxes, but ${rest}`
}

/**
 * What the Review card says of a rule that can never be met
 * (showWhenUnanswerable). Without a box these are the app's sentences word for
 * word (send/words.ts); with one, the box and its document are named, as
 * showWhenOnNonSignerWords names them, so two such boxes read differently.
 * The controller always signs, so an unnamed one is "Signer N".
 */
export function showWhenFaultWords(
  fault: ShowWhenFault,
  name: string,
  index: number,
  controllerName: string,
  controllerIndex: number,
  value: string,
  box?: { prompt: string; document: string },
): string {
  const who = name.trim() || `Signer ${index + 1}`
  const controller = controllerName.trim() || `Signer ${controllerIndex + 1}`
  const subject = box ? `${who}'s “${box.prompt}” box on ${box.document} is` : `${who} has a box`
  switch (fault) {
    case 'no-answer':
      return `${subject} shown only when another box has an answer, but no answer is chosen. Type one under “Required answer”, or choose “Always shown”.`
    case 'not-an-option':
      return `${subject} shown only when ${controller} chooses “${value}”, which is no longer one of the choices, so it would never be shown. Under “Required answer”, choose one of the choices, or choose “Always shown”.`
    case 'other-person':
      // A signer is shown only their own boxes, and the server refuses the
      // rule (review 4, docs-1).
      return `${subject} shown only when ${controller} answers one of their boxes, but ${who} sees only their own boxes when signing, so it would never be shown. Under “Show only when”, choose one of ${who}'s boxes or “Always shown”.`
    case 'loop':
      return `${subject} shown only when another box is answered, and that box is shown only when this one is, so neither could ever be shown. Under “Show only when”, choose “Always shown” for one of them.`
  }
}

/**
 * What the Review card says of a radio or dropdown whose options the server
 * refuses (optionsRefused), in the app's words (send/words.ts optionsTooFew
 * and optionsRepeated). Without a box, the person is named alone.
 */
/** What a notice's "Show the box" brings into view. */
export type Reveal = { part: 'show-when' } | { part: 'options'; fault: OptionsFault }

/**
 * The element in a box's roster row that a notice asks the sender to change.
 * A rule's notice asks under "Show only when". A repeated option's notice
 * asks for the later copy to be changed, and a too-few notice for the first
 * empty option; with no empty one there is only the list to show. Options
 * are counted as the row renders them, one .option-row each.
 */
export function revealSelector(look: Reveal, options: readonly string[]): string {
  if (look.part === 'show-when') return '.show-when'
  const trimmed = options.map((o) => o.trim())
  const at = look.fault === 'repeated'
    ? trimmed.findIndex((o, i) => o !== '' && trimmed.indexOf(o) !== i)
    : trimmed.findIndex((o) => o === '')
  return at < 0 ? '.option-list' : `.option-list > .option-row:nth-child(${at + 1}) input`
}

export function optionsFaultWords(
  fault: OptionsFault,
  name: string,
  index: number,
  repeated: string,
  box?: { prompt: string; document: string },
): string {
  const who = name.trim() || `Signer ${index + 1}`
  const subject = box ? `${who}'s “${box.prompt}” box on ${box.document}` : `${who}'s box`
  return fault === 'too-few'
    ? `${subject} needs at least two options. Type a second option.`
    : `${subject} has the option “${repeated}” twice. Change or remove one of them.`
}

/**
 * Hide this box until another box has a particular answer.
 *
 * The controller is named by its draft key here and turned into recipient and
 * field indexes on send, because those indexes are what the server accepts
 * before field ids exist.
 */
export function ShowWhenPicker({
  recipients,
  fieldKey,
  value,
  onChange,
}: {
  recipients: Draft[]
  fieldKey: string
  value?: ShowWhenDraft
  onChange: (next: ShowWhenDraft | undefined) => void
}) {
  // Only a signing party's boxes are answered: anybody else is sent with
  // none. A signer is shown only their own boxes, so only the owner's boxes
  // are offered (review 4, docs-1). A rule already set on another party's
  // box stays in the list, so the choice can be seen and changed
  // (review-2-app R3); the Review card says why it cannot be sent.
  const owner = locateFieldKey(recipients, fieldKey)?.ri
  const controllers: { key: string; label: string; kind: FieldKind; options: string[] }[] = []
  recipients.forEach((r, ri) => {
    r.fields.forEach((f, fi) => {
      if (f.key === fieldKey || !canControlShowWhen(f.kind)) return
      const signs = signsDocument(r.role)
      if ((!signs || ri !== owner) && f.key !== value?.fieldKey) return
      const who = r.name.trim() || `${signs ? 'Signer' : 'Person'} ${ri + 1}`
      controllers.push({
        key: f.key,
        label: `${who} · ${fieldDisplayName(r.fields, fi)}${signs ? '' : ' (does not sign)'}`,
        kind: f.kind,
        options: f.options,
      })
    })
  })
  if (controllers.length === 0) return null

  const selected = controllers.find((c) => c.key === value?.fieldKey)

  return (
    <div className="show-when" onClick={(e) => e.stopPropagation()}>
      <label className="small faint" htmlFor={`show-when-${fieldKey}`}>Show only when</label>
      <select
        id={`show-when-${fieldKey}`}
        value={value?.fieldKey ?? ''}
        onChange={(e) => {
          const key = e.target.value
          if (!key) {
            onChange(undefined)
            return
          }
          const ctrl = controllers.find((c) => c.key === key)
          if (!ctrl) {
            onChange(undefined)
            return
          }
          if (ctrl.kind === 'checkbox') {
            onChange({ fieldKey: key, op: 'checked' })
            return
          }
          onChange({ fieldKey: key, op: 'eq', value: ctrl.options[0] || '' })
        }}
      >
        <option value="">Always shown</option>
        {controllers.map((c) => (
          <option key={c.key} value={c.key}>{c.label}</option>
        ))}
      </select>
      {/* Visible, because the Review card's notices send the sender to
          "Required answer" by name, and it was only an aria-label: nothing
          on screen was called that (review 4, design-2). The app shows the
          same label (compose/place/words.ts requiredAnswer). */}
      {selected && selected.kind !== 'checkbox' && (kindNeedsOptions(selected.kind) || selected.kind === 'text') && (
        <label className="small faint" htmlFor={`show-when-answer-${fieldKey}`}>Required answer</label>
      )}
      {selected && selected.kind !== 'checkbox' && kindNeedsOptions(selected.kind) && (
        <select
          id={`show-when-answer-${fieldKey}`}
          value={value?.value ?? ''}
          onChange={(e) => onChange(value ? { ...value, op: 'eq', value: e.target.value } : undefined)}
        >
          {/* An answer that was renamed or removed after the rule was set.
              Without this the list shows its first choice as if it were
              chosen, and picking that choice changes nothing. */}
          {!selected.options.includes(value?.value ?? '') && (
            <option value={value?.value ?? ''} disabled>Choose an answer</option>
          )}
          {selected.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}
      {selected && selected.kind === 'text' && (
        <input
          id={`show-when-answer-${fieldKey}`}
          type="text"
          placeholder="the answer that shows this box"
          value={value?.value ?? ''}
          onChange={(e) => onChange(value ? { ...value, op: 'eq', value: e.target.value } : undefined)}
        />
      )}
    </div>
  )
}
